import { useLayoutEffect, useRef } from 'react'
import { useShallow } from 'zustand/react/shallow'

import { useAppStore } from '../../store/appStore/appStore'
import {
  computeBrowserDocumentTitle,
  isIdleBrowserDocumentTitle,
} from '../../store/appStore/browserDocumentTitle/computeBrowserDocumentTitle'

const pickTitleInput = (s: {
  browserDocumentTitle: {
    staticTitle: string | null
    viewLabel: string | null
    detailEntityDisplay: string | null
    detailDrawerSuffix: string | null
  }
}) => {
  const b = s.browserDocumentTitle
  return {
    staticTitle: b.staticTitle,
    viewLabel: b.viewLabel,
    detailEntityDisplay: b.detailEntityDisplay,
    detailDrawerSuffix: b.detailDrawerSuffix,
  }
}

/**
 * Syncs {@link browserDocumentTitle} to `document.title` in a layout effect (before paint).
 *
 * When the slice has no `staticTitle` or `viewLabel`, {@link isIdleBrowserDocumentTitle}
 * is true. In that case, if a non-idle title has already been written to the tab in this
 * component instance, `document.title` is left unchanged so the last resolved title remains
 * until callers populate the slice again (for example after `resetForNavigation`).
 */
export const DocumentTitleSync = () => {
  const { title, isIdle } = useAppStore(
    useShallow(s => {
      const input = pickTitleInput(s)
      return {
        title: computeBrowserDocumentTitle(input),
        isIdle: isIdleBrowserDocumentTitle(input),
      }
    }),
  )
  const hasAppliedNonIdleTitleRef = useRef(false)

  useLayoutEffect(() => {
    if (!isIdle) {
      document.title = title
      hasAppliedNonIdleTitleRef.current = true
      return
    }
    if (!hasAppliedNonIdleTitleRef.current) {
      document.title = title
    }
  }, [title, isIdle])

  return null
}
